import React, { Component, Fragment } from "react";
import SectionTitle from "../../elements/sectionTitle";
import { YieldApi } from "../../../api/yield";
import { format, addYears, differenceInDays } from "date-fns";
import {
  ThemeProvider,
  CircularProgress,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
  Select,
  FormControl,
  FormHelperText,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { State } from "./defs";
import { baseTheme } from "mui-theme";
import { toast } from "react-toastify";
import FormulaOverides from "./formulaOverides";
import withRouter from "../../withRouter";
import Popup from "../../../reusable-components/popup/popup";
import TimezoneSelect from "reusable-components/timezoneselect";
import { connect } from "react-redux";
import { setActiveHotel } from "redux/Slice/hotelSlice";
import { AuthService } from "App/services";
import { validateRTFormula } from "App/utils/formulaValidator";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import * as Yup from "yup";
import { Formik } from "formik";
import { ConfigApi } from "api/config";

interface Props {
  activeHotel: any;
  user: any;
  router: any;
  setActiveHotel: any;
  authData?: any;
}

interface Formula {
  label: string;
  formula: string;
  start: string;
  end: string;
  order?: number;
}

const validationSchema = Yup.object().shape({
  // Info fields
  fullName: Yup.string().required("Mandatory field"),
  name: Yup.string().required("Mandatory field"),
  level: Yup.string().required("Mandatory field"),
  currencyCode: Yup.string().required("Mandatory field"),
  timezone: Yup.string(),

  // Meta fields
  totalRooms: Yup.number()
    .required("Mandatory field")
    .positive("Total rooms must be greater than 0")
    .max(999999999999999, "Value too large"),
  defaultBar: Yup.number()
    .required("Mandatory field")
    .min(0, "Negative number not allowed")
    .max(999, "Default base rate can't be greater than 999"),
  pickupRange: Yup.number().min(0, "Negative number not allowed"),
  otaPickupRange: Yup.number().min(0, "Negative number not allowed"),
  hasRewards: Yup.boolean(),
  isPublishingInventory: Yup.boolean(),
  isSyncOn: Yup.boolean(),

  // CM fields
  cmId: Yup.string(),
  cmHotelId: Yup.string().test(
    "valid-cm-hotel-id",
    "Only Alphabets, numbers and spaces allowed.",
    (value) =>
      !value || /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/.test(value),
  ),
  cmUserId: Yup.string().test(
    "valid-cm-user-id",
    "Only Alphabets, numbers and spaces allowed.",
    (value) =>
      !value || /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/.test(value),
  ),
  flexTable: Yup.string().test(
    "valid-flex-table",
    "Only Alphabets, numbers and spaces allowed.",
    (value) =>
      !value || /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/.test(value),
  ),
  OWSflexTable: Yup.string().test(
    "valid-ows-flex-table",
    "Only Alphabets, numbers and spaces allowed.",
    (value) =>
      !value || /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/.test(value),
  ),
  BRflexTableRate: Yup.string(),
  OWSflexTableRate: Yup.string(),

  // PMS fields
  pmsId: Yup.string(),
  inventoryType: Yup.string(),
  pmsHotelCode: Yup.string().test(
    "valid-pms-hotel-code",
    "Only Alphabets, numbers and spaces allowed.",
    (value) =>
      !value || /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/.test(value),
  ),
  urlEndpoint: Yup.string(),

  // OWS fields
  owsLabel: Yup.string(),
  formula: Yup.string()
    .required("Mandatory field")
    .test("valid-formula", "Invalid formula", validateRTFormula),
  seasonnalFormula: Yup.boolean(),

  // Rate Matrix
  isRateMatrix: Yup.boolean(),
  _formula_overrides_version: Yup.number(),
});

class NewHotelConfig extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    console.log("Constructor props:", props);

    // Initialize with activeHotel if available
    const hotelData = props.activeHotel;
    this.state = {
      hotel: hotelData
        ? JSON.parse(JSON.stringify(hotelData))
        : {
            fullName: "",
            name: "",
            userName: "NO DATA",
            password: "NO DATA",
            meta: {
              otaPickupRange: 0,
              pickupRange: 0,
              isSyncOn: false,
              hasRewards: true,
              isPublishingInventory: false,
            },
            cm: {
              id: "",
              APISettings: {
                OWSflexTable: "",
                flexTable: "",
              },
              channelConfig: {
                ows: {
                  label: "",
                },
              },
            },
            pms: {
              id: "none",
              inventoryType: "manual",
              apiSettings: {
                hotelCode: "",
                urlEndpoint: "",
              },
            },
          },
      deleteFormula: {
        hotelId: 0,
        start: "",
        end: "",
        label: "",
        deleteLabel: "",
      },
      currentSetIndex: 1,
      displayUpdateRateWarningOnsave: false,
      warningDialogOpened: false,
      setGuestline: false,
      onSave: false,
      hover: false,
      disableOTAPickup: false,
      isSyncOn: hotelData?.meta?.isSyncOn || false,
      openRewardsPopup: false,
      rewardsPopupToBeOpen: false,
      originalHasRewardsValue: hotelData?.meta?.hasRewards ?? true,
    };
  }

  async componentDidMount() {
    console.log("ComponentDidMount - activeHotel:", this.props.activeHotel);
    const pathname = window.location.pathname;
    const parts = pathname.split("/");
    const hotelId = parseInt(parts[1]);

    // If we have a valid hotel ID and activeHotel doesn't match
    if (
      !isNaN(hotelId) &&
      (!this.props.activeHotel || this.props.activeHotel.hotelId !== hotelId)
    ) {
      console.log("Fetching hotel data for ID:", hotelId);
      // Fetch hotel data
      try {
        //refresh user
        await AuthService.revanista.user.identify();
        const hotelData = await ConfigApi.getHotelConfig(hotelId);
        console.log("Fetched hotel data:", hotelData);
        if (hotelData) {
          // Update Redux store
          this.props.setActiveHotel(hotelData);

          // Initialize component state
          const initialHotel = JSON.parse(JSON.stringify(hotelData));
          this.setState({
            hotel: initialHotel,
            isSyncOn: initialHotel.meta?.isSyncOn || false,
            setGuestline: initialHotel.cm?.id === "guestline",
          });
        }
      } catch (err) {
        console.error("Error loading hotel data:", err);
      }
    }
  }

  async componentDidUpdate(prevProps: Props) {
    if (prevProps.activeHotel !== this.props.activeHotel) {
      const hotelData = this.props.activeHotel;

      if (hotelData && Object.keys(hotelData).length > 0) {
        const initialHotel = JSON.parse(JSON.stringify(hotelData));
        this.setState({
          hotel: initialHotel,
          isSyncOn: initialHotel.meta?.isSyncOn || false,
          setGuestline: initialHotel.cm?.id === "guestline",
        });
      }
    }
  }

  // Add this helper function for numeric input (prevents non-numeric characters)
  handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Allow only numbers
    if (/^\d*$/.test(value)) {
      e.target.value = value;
      e.target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  // Update this method to work with Formik values
  onHotelUpdate = (updatedHotel: any) => {
    // Create a new hotel object with all nested properties
    const hotel = {
      ...this.state.hotel,
      cm: {
        ...this.state.hotel.cm,
        channelConfig: {
          ...this.state.hotel.cm.channelConfig,
          ows: {
            ...this.state.hotel.cm.channelConfig.ows,
            SeasonnalConfig: {
              ...updatedHotel.cm.channelConfig.ows.SeasonnalConfig,
              formulas: [
                ...updatedHotel.cm.channelConfig.ows.SeasonnalConfig.formulas,
              ] as Formula[],
            },
          },
        },
      },
    };

    this.setState({ hotel });
  };

  // Delete this method as it's replaced by Formik validation
  // onBlurOfInputFields() {
  //   // Remove this entire method
  // }

  // Update save config method to work with Formik
  saveConfigAfterValidation = async () => {
    // The popup logic is now handled in the Formik onSubmit handler.
    // This function will only be called after the popup is confirmed.
    this.setState({ openRewardsPopup: false });

    // Create deep copies of all nested objects
    const hotel = { ...this.state.hotel };
    const deleteFormula = { ...this.state.deleteFormula };

    //Reorder Override Formula
    if (hotel.cm.channelConfig.ows.SeasonnalConfig?.formulas?.length > 0) {
      // Create new array with modified objects
      const updatedFormulas =
        hotel.cm.channelConfig.ows.SeasonnalConfig.formulas.map(
          (item: Formula) => ({
            ...item,
            order: Number(item.formula.replace(/BR/g, "").replace(/\s/g, "")),
          }),
        );

      // Sort the new array
      updatedFormulas.sort(
        (a: Formula, b: Formula) =>
          parseFloat(String(b.order)) - parseFloat(String(a.order)),
      );

      // Create new SeasonnalConfig object with the updated formulas
      hotel.cm.channelConfig.ows.SeasonnalConfig = {
        ...hotel.cm.channelConfig.ows.SeasonnalConfig,
        formulas: updatedFormulas,
      };
    }

    this.setState({ hotel });
    const warningDialogOpened = this.state.displayUpdateRateWarningOnsave;

    const updateConfigPromise = toast.promise(
      ConfigApi.updateHotelConfig({
        hotel: hotel,
        start: format(new Date(), "yyyy-MM-dd"),
      }),
      {
        pending: "Saving hotel config...",
        error: "Error saving hotel config",
        success: "Hotel config saved",
      },
    );
    updateConfigPromise
      .then(() => {
        // On success, this is no longer needed as Formik handles the dirty state
      })
      .catch(() => {
        return;
      });
    this.props.setActiveHotel(hotel);
    updateConfigPromise.catch(() => {
      return;
    });
    if (warningDialogOpened || deleteFormula.deleteLabel) {
      if (deleteFormula.deleteLabel) {
        toast.promise(
          YieldApi.bulkUpdateOwsOverride(deleteFormula).then(() => {
            this.setState({
              deleteFormula: {
                hotelId: 0,
                start: "",
                end: "",
                label: "",
                deleteLabel: "",
              },
            });
          }),
          {
            pending: "Deleting OWS Formula override...",
            error: "Error deleting OWS Formula override",
            success: "OWS Formula override deleted",
          },
        );
      } else {
        if (warningDialogOpened) {
          let formulaLabel;
          if (typeof hotel.cm.channelConfig.ows.label != "undefined") {
            if (hotel.cm.channelConfig.ows.label != "") {
              formulaLabel = hotel.cm.channelConfig.ows.label;
            } else {
              formulaLabel = "Default";
            }
          } else {
            formulaLabel = "Default";
          }
          const updateDefaultFormula = {
            hotelId: this.state.hotel.hotelId,
            start: format(new Date(), "yyyy-MM-dd"),
            end: format(addYears(new Date(), 2), "yyyy-MM-dd"),
            label: formulaLabel,
          };

          toast.promise(YieldApi.updateOwsOverride(updateDefaultFormula), {
            pending: "Updating Default OWS differential...",
            error: "Error updating Default OWS differential",
            success: "Default OWS differential updated",
          });
        }
      }
    }
  };

  cancelChanges = async () => {
    this.setState({
      deleteFormula: {
        hotelId: 0,
        start: "",
        end: "",
        label: "",
        deleteLabel: "",
      },
      hotel: this.props.activeHotel,
      currentSetIndex: this.state.currentSetIndex + 1,
    });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };
  closewarningDialog = () => {
    const warningDialogOpened = false;
    this.setState({ warningDialogOpened });
  };

  // onBlurOfInputFields removed. All validation is handled by Formik/Yup.

  // This entire function is now obsolete and should be removed.
  // Formik handles state changes for its fields.
  // handleChange = (event: any) => { ... };

  handleOnMouseOver = () => {
    this.setState({ hover: true });
  };

  handleOnMouseOut = () => {
    this.setState({ hover: false });
  };

  handleOnCheckboxCheck = () => {
    this.setState({
      isSyncOn: !this.state.isSyncOn,
      disableOTAPickup: !this.state.disableOTAPickup,
    });
  };

  closeRewardsPopup = () => {
    this.setState({ openRewardsPopup: false });
  };

  // Convert to arrow function
  handleTimeZone = (e: { value: string }): void => {
    const hotel = this.state.hotel;
    const nameValue = e.value;
    hotel.info.timezone = nameValue;

    this.setState({
      hotel,
    });
  };

  render() {
    const date = "2024-04-15";
    const onboardDate = this.props.activeHotel?.hotelOnboardDate || date;
    const a = new Date(onboardDate);
    const b = new Date(format(new Date(), "yyyy-MM-dd"));
    console.log("activeHotel", this.props.activeHotel);
    console.log("hotel to render", this.state.hotel);
    const diff = differenceInDays(b, a);
    const otaPickupRange = this.state.hotel?.meta?.otaPickupRange || 0;

    if (otaPickupRange > diff) {
      console.log("is greater", diff);
    } else {
      console.log("is lesss", diff);
    }

    let adminPerm: boolean = hasPermission(ACTIONS.EDIT_CONFIG);
    let revManagerPerm: boolean = hasPermission(ACTIONS.EDIT_OWS);

    if (
      !this.state.hotel ||
      !this.state.hotel.info ||
      !this.props.activeHotel
    ) {
      return (
        <ThemeProvider theme={baseTheme}>
          <div className="content">
            <CircularProgress color="inherit" id="circular-progress" />
          </div>
        </ThemeProvider>
      );
    } else {
      // Extract initial values from state
      const initialValues = {
        // Info fields
        fullName: this.state.hotel.info?.fullName || "",
        name: this.state.hotel.info?.name || "",
        level: this.state.hotel.info?.level || "",
        currencyCode: this.state.hotel.info?.currencyCode || "EUR",
        timezone:
          typeof this.state.hotel.info?.timezone === "string"
            ? this.state.hotel.info.timezone
            : this.state.hotel.info?.timezone?.value ||
              Intl.DateTimeFormat().resolvedOptions().timeZone,

        // Meta fields
        totalRooms: this.state.hotel.meta?.totalRooms || "",
        defaultBar: this.state.hotel.meta?.defaultBar || "",
        // Fix for pickup ranges - ensure 0 values are preserved
        otaPickupRange:
          this.state.hotel.meta?.otaPickupRange != null
            ? this.state.hotel.meta.otaPickupRange
            : "",
        pickupRange:
          this.state.hotel.meta?.pickupRange != null
            ? this.state.hotel.meta.pickupRange
            : "",
        hasRewards: this.state.hotel.meta?.hasRewards ?? true,
        isPublishingInventory:
          this.state.hotel.meta?.isPublishingInventory ?? false,
        isSyncOn: this.state.hotel.meta?.isSyncOn ?? false,

        // CM fields
        cmId: this.state.hotel.cm?.id || "",
        cmHotelId: this.state.hotel.cm?.APISettings?.hotelId || "",
        cmUserId: this.state.hotel.cm?.credentials?.userId || "",
        flexTable: this.state.hotel.cm?.APISettings?.flexTable || "",
        OWSflexTable: this.state.hotel.cm?.APISettings?.OWSflexTable || "",
        BRflexTableRate:
          this.state.hotel.cm?.APISettings?.BRflexTableRate || "",
        OWSflexTableRate:
          this.state.hotel.cm?.APISettings?.OWSflexTableRate || "",

        // PMS fields
        pmsProvider: this.state.hotel.pms?.id || "",
        inventoryType: this.state.hotel.pms?.inventoryType || "",
        pmsHotelCode: this.state.hotel.pms?.apiSettings?.hotelCode || "",
        urlEndpoint: this.state.hotel.pms?.apiSettings?.urlEndpoint || "",

        // OWS fields
        owsLabel: this.state.hotel.cm?.channelConfig?.ows?.label || "",
        formula: this.state.hotel.cm?.channelConfig?.ows?.formula || "",
        seasonnalFormula:
          this.state.hotel.cm?.channelConfig?.ows?.seasonnalFormula || false,

        // Rate Matrix
        isRateMatrix: this.state.hotel.suggestionParams?.isEnabled ?? false,
        _formula_overrides_version: 1,
      };

      return (
        <ThemeProvider theme={baseTheme}>
          <Popup
            isPopupOpen={this.state.openRewardsPopup}
            closeOnOutsideClick={false}
            title="Disabling Rewards"
            content={[
              "Are you sure you want to disable rewards?",
              "The rewards rate plans in channel manager will not be automatically removed",
            ]}
            action={() => {
              // User confirmed, proceed with saving.
              this.setState({ openRewardsPopup: false }, () => {
                this.saveConfigAfterValidation();
              });
            }}
            actionName="Disable Rewards"
            cancelAction={this.closeRewardsPopup}
            cancelName="Cancel"
          />
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize // Important: Allows Formik to update with new state
            onSubmit={(values, { setSubmitting }) => {
              // Always build the complete, updated hotel object from the form values first.
              const updatedHotel = { ...this.state.hotel };

              // Info fields
              if (!updatedHotel.info) updatedHotel.info = {};
              updatedHotel.info.fullName = values.fullName;
              updatedHotel.info.name = values.name;
              updatedHotel.info.level = values.level;
              updatedHotel.info.currencyCode = values.currencyCode;
              updatedHotel.info.timezone = values.timezone;

              // Meta fields
              if (!updatedHotel.meta) updatedHotel.meta = {};
              updatedHotel.meta.totalRooms = values.totalRooms;
              updatedHotel.meta.defaultBar = values.defaultBar;
              updatedHotel.meta.otaPickupRange = values.otaPickupRange;
              updatedHotel.meta.pickupRange = values.pickupRange;
              updatedHotel.meta.hasRewards = values.hasRewards;
              updatedHotel.meta.isPublishingInventory =
                values.isPublishingInventory;
              updatedHotel.meta.isSyncOn = values.isSyncOn;

              // CM fields
              if (!updatedHotel.cm) updatedHotel.cm = {};
              updatedHotel.cm.id = values.cmId || updatedHotel.cm.id;

              if (!updatedHotel.cm.APISettings)
                updatedHotel.cm.APISettings = {};
              updatedHotel.cm.APISettings.hotelId = values.cmHotelId;
              updatedHotel.cm.APISettings.flexTable = values.flexTable;
              updatedHotel.cm.APISettings.OWSflexTable = values.OWSflexTable;
              updatedHotel.cm.APISettings.BRflexTableRate =
                values.BRflexTableRate;
              updatedHotel.cm.APISettings.OWSflexTableRate =
                values.OWSflexTableRate;

              if (!updatedHotel.cm.credentials)
                updatedHotel.cm.credentials = {};
              updatedHotel.cm.credentials.userId = values.cmUserId;

              if (!updatedHotel.cm.channelConfig)
                updatedHotel.cm.channelConfig = {};
              if (!updatedHotel.cm.channelConfig.ows)
                updatedHotel.cm.channelConfig.ows = {};
              updatedHotel.cm.channelConfig.ows.label = values.owsLabel;
              updatedHotel.cm.channelConfig.ows.formula = values.formula;
              updatedHotel.cm.channelConfig.ows.seasonnalFormula =
                values.seasonnalFormula;

              // PMS fields
              if (!updatedHotel.pms) updatedHotel.pms = {};
              updatedHotel.pms.id = values.pmsProvider || updatedHotel.pms.id;
              updatedHotel.pms.inventoryType = values.inventoryType;

              if (!updatedHotel.pms.apiSettings)
                updatedHotel.pms.apiSettings = {};
              updatedHotel.pms.apiSettings.hotelCode = values.pmsHotelCode;
              updatedHotel.pms.apiSettings.urlEndpoint = values.urlEndpoint;

              // Rate Matrix
              if (!updatedHotel.suggestionParams)
                updatedHotel.suggestionParams = {};
              updatedHotel.suggestionParams.isEnabled = values.isRateMatrix;

              // Check if the rewards setting is being disabled by comparing redux state with form values
              if (
                this.props.activeHotel.meta.hasRewards === true &&
                values.hasRewards === false &&
                !this.state.openRewardsPopup
              ) {
                // If so, update the state with the new data AND show the popup.
                // The save will be triggered by the popup's confirmation button, which will use the updated state.
                this.setState({ hotel: updatedHotel, openRewardsPopup: true });
                setSubmitting(false);
                return;
              }

              // For all other cases (or after popup confirmation), update the state and save immediately.
              this.setState({ hotel: updatedHotel }, () => {
                this.saveConfigAfterValidation().finally(() => {
                  setSubmitting(false);
                });
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              dirty,
            }) => {
              // Auto-sync OTA pickup range whenever the form is (re)initialized
              // and sync is enabled. This avoids relying on manual user input.
              if (
                values.isSyncOn &&
                values.otaPickupRange !== values.pickupRange
              ) {
                setFieldValue("otaPickupRange", values.pickupRange, false);
              }

              return (
                <form onSubmit={handleSubmit}>
                  <div className="content">
                    {revManagerPerm == false ? (
                      <p
                        style={{
                          color: "red",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        This page is admin locked. Please contact us should you
                        need something changed.
                      </p>
                    ) : null}

                    <SectionTitle {...{ value: "Global Settings" }} />
                    <br />
                    <div style={{ flexGrow: "1" }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            style={{ width: "100%" }}
                            fullWidth
                            id="outlined-basic"
                            label="Hotel Name"
                            variant="outlined"
                            name="fullName"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.fullName}
                            error={touched.fullName && Boolean(errors.fullName)}
                            helperText={
                              touched.fullName &&
                              typeof errors.fullName === "string"
                                ? errors.fullName
                                : undefined
                            }
                            inputProps={{ maxLength: 40 }}
                            disabled={revManagerPerm == false}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            // className="input-field"
                            style={{
                              width: "100%",
                            }}
                            fullWidth
                            id="outlined-basic"
                            label="Hotel Short Name"
                            variant="outlined"
                            name="name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                            error={touched.name && Boolean(errors.name)}
                            helperText={
                              touched.name && typeof errors.name === "string"
                                ? errors.name
                                : undefined
                            }
                            inputProps={{ maxLength: 20 }}
                            disabled={revManagerPerm == false}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            style={{ width: "100%" }}
                            disabled
                            fullWidth
                            id="outlined-basic"
                            label="Hotel ID"
                            variant="outlined"
                            name="hotelId"
                            value={this.state.hotel.hotelId}
                          />
                        </Grid>

                        <br />

                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl
                            fullWidth
                            error={touched.level && Boolean(errors.level)}
                          >
                            <InputLabel>Contract Type</InputLabel>
                            <Select
                              value={values.level}
                              fullWidth
                              label="Contract Type"
                              variant="outlined"
                              name="level"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.level && Boolean(errors.level)}
                              disabled={adminPerm == false}
                            >
                              <MenuItem value="Pro">Pro</MenuItem>
                              <MenuItem value="Plus">Plus</MenuItem>
                              <MenuItem value="Assist">Assist</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.level && typeof errors.level === "string"
                                ? errors.level
                                : undefined}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            style={{
                              width: "100%",
                            }}
                            disabled={
                              revManagerPerm == false ||
                              this.state.hotel.pms.inventoryType === "live"
                            }
                            fullWidth
                            id="outlined-basic"
                            label="Total Number of Rooms"
                            variant="outlined"
                            name="totalRooms"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.totalRooms}
                            error={
                              touched.totalRooms && Boolean(errors.totalRooms)
                            }
                            helperText={
                              touched.totalRooms &&
                              typeof errors.totalRooms === "string"
                                ? errors.totalRooms
                                : undefined
                            }
                            inputProps={{
                              maxLength: 20,
                              min: 0,
                              max: 999999999999999,
                              readOnly: !revManagerPerm ? true : false,
                            }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Rate Matrix</InputLabel>
                            <Select
                              value={values.isRateMatrix}
                              style={{ width: "100%" }}
                              disabled={revManagerPerm == false}
                              fullWidth
                              id="outlined"
                              label="Rate Matrix"
                              variant="outlined"
                              name="isRateMatrix"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <MenuItem value={true as any}>Enabled</MenuItem>
                              <MenuItem value={false as any}>Disabled</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Rewards Site Function</InputLabel>
                            <Select
                              value={values.hasRewards}
                              fullWidth
                              label="Rewards Site Function"
                              variant="outlined"
                              name="hasRewards"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={revManagerPerm == false}
                            >
                              <MenuItem value={true as any}>Enabled</MenuItem>
                              <MenuItem value={false as any}>Disabled</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Currency</InputLabel>
                            <Select
                              value={values.currencyCode}
                              fullWidth
                              label="Currency"
                              variant="outlined"
                              name="currencyCode"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={revManagerPerm == false}
                            >
                              <MenuItem value="EUR">EUR</MenuItem>
                              <MenuItem value="GBP">GBP</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TimezoneSelect
                            value={values.timezone}
                            onChange={(e) => {
                              setFieldValue("timezone", e.value);
                            }}
                            disabled={revManagerPerm == false}
                          />
                        </Grid>
                      </Grid>
                    </div>
                    <br />

                    <SectionTitle {...{ value: "Default Config" }} />
                    <br />
                    <div style={{ flexGrow: "1" }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            // className="input-field"
                            style={{
                              width: "100%",
                            }}
                            fullWidth
                            id="outlined-basic"
                            label="Default Base Rate"
                            variant="outlined"
                            type="number"
                            name="defaultBar"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.defaultBar}
                            error={
                              touched.defaultBar && Boolean(errors.defaultBar)
                            }
                            helperText={
                              touched.defaultBar &&
                              typeof errors.defaultBar === "string"
                                ? errors.defaultBar
                                : undefined
                            }
                            inputProps={{ maxLength: 20 }}
                            disabled={revManagerPerm == false}
                            required
                          />
                        </Grid>
                      </Grid>

                      <br />

                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            style={{
                              width: "100%",
                            }}
                            fullWidth
                            id="outlined-basic"
                            label="Default Inventory Pickup Range (in days)"
                            variant="outlined"
                            name="pickupRange"
                            type="number"
                            onChange={(e) => {
                              handleChange(e);
                              // If sync is on, mirror the value to OTA pickup range
                              if (values.isSyncOn) {
                                setFieldValue("otaPickupRange", e.target.value);
                              }
                            }}
                            onBlur={handleBlur}
                            value={values.pickupRange}
                            error={
                              touched.pickupRange && Boolean(errors.pickupRange)
                            }
                            helperText={
                              touched.pickupRange &&
                              typeof errors.pickupRange === "string"
                                ? errors.pickupRange
                                : undefined
                            }
                            inputProps={{
                              maxLength: 20,
                            }}
                            disabled={revManagerPerm == false}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            style={{
                              width: "100%",
                            }}
                            fullWidth
                            id="outlined-basic"
                            label="Default OTA Pickup Range (in days)"
                            variant="outlined"
                            name="otaPickupRange"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.otaPickupRange}
                            error={
                              touched.otaPickupRange &&
                              Boolean(errors.otaPickupRange)
                            }
                            helperText={
                              touched.otaPickupRange &&
                              typeof errors.otaPickupRange === "string"
                                ? errors.otaPickupRange
                                : undefined
                            }
                            inputProps={{ maxLength: 20 }}
                            disabled={
                              revManagerPerm == false || values.isSyncOn
                            }
                            required
                          />
                        </Grid>
                        <Fragment>
                          <div
                            className="form-check"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              marginTop: "24px",
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.isSyncOn}
                                  onChange={(e) => {
                                    handleChange(e);
                                    const checked = (
                                      e.target as HTMLInputElement
                                    ).checked;
                                    // When turning on sync, immediately align OTA range to inventory range
                                    if (checked) {
                                      setFieldValue(
                                        "otaPickupRange",
                                        values.pickupRange,
                                      );
                                    }
                                  }}
                                  name="isSyncOn"
                                  color="primary"
                                  disabled={
                                    revManagerPerm == false ? true : false
                                  }
                                />
                              }
                              label="Synchronize date ranges"
                            />
                          </div>
                        </Fragment>
                      </Grid>
                    </div>
                    <br />

                    <SectionTitle {...{ value: "Channel Manager Settings" }} />
                    <br />
                    <div style={{ flexGrow: "1" }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>CM Provider</InputLabel>
                            <Select
                              disabled={adminPerm == false ? true : false}
                              fullWidth
                              label="CM Provider"
                              variant="outlined"
                              name="cmId"
                              onChange={handleChange}
                              value={values.cmId}
                            >
                              <MenuItem value="siteMinder">SiteMinder</MenuItem>
                              <MenuItem value="channelRush">
                                Channel Rush
                              </MenuItem>
                              <MenuItem value="guestline">Guestline</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <Tooltip
                            title="If enabled, Revanista will allow to publish availability to Channel Manager. If disabled, property's PMS should be sending availability to Channel Manager."
                            arrow
                            placement="right"
                          >
                            <FormControl fullWidth>
                              <InputLabel>Publish Inventory</InputLabel>
                              <Select
                                value={values.isPublishingInventory}
                                disabled={
                                  (this.state.hotel.pms.inventoryType ===
                                    "live" &&
                                    this.state.hotel.pms.id !== "none") ||
                                  this.state.hotel.cm.id === "guestline" ||
                                  adminPerm == false
                                }
                                fullWidth
                                label="Publish Inventory"
                                variant="outlined"
                                name="isPublishingInventory"
                                onChange={handleChange}
                              >
                                <MenuItem value={true as any}>Enabled</MenuItem>
                                <MenuItem value={false as any}>
                                  Disabled
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            style={{
                              width: "100%",
                            }}
                            fullWidth
                            id="outlined-basic"
                            label="Hotel Code"
                            variant="outlined"
                            name="cmHotelId"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.cmHotelId}
                            error={
                              touched.cmHotelId && Boolean(errors.cmHotelId)
                            }
                            helperText={
                              touched.cmHotelId &&
                              typeof errors.cmHotelId === "string"
                                ? errors.cmHotelId
                                : undefined
                            }
                            disabled={
                              this.state.setGuestline === true || !adminPerm
                            }
                            inputProps={{
                              maxLength: 20,
                            }}
                            required
                          />
                        </Grid>

                        {this.state.setGuestline === false ? (
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              style={{ width: "100%" }}
                              disabled={adminPerm == false}
                              fullWidth
                              id="outlined-basic"
                              label="User Name"
                              variant="outlined"
                              name="cmUserId"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.cmUserId}
                              error={
                                touched.cmUserId && Boolean(errors.cmUserId)
                              }
                              helperText={
                                touched.cmUserId &&
                                typeof errors.cmUserId === "string"
                                  ? errors.cmUserId
                                  : undefined
                              }
                              inputProps={{ maxLength: 20 }}
                              required
                            />
                          </Grid>
                        ) : (
                          <>
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                style={{
                                  width: "100%",
                                }}
                                fullWidth
                                disabled={!adminPerm}
                                id="outlined-basic"
                                label="Base Rate Flex Table"
                                variant="outlined"
                                name="flexTable"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.flexTable}
                                error={
                                  touched.flexTable && Boolean(errors.flexTable)
                                }
                                helperText={
                                  touched.flexTable &&
                                  typeof errors.flexTable === "string"
                                    ? errors.flexTable
                                    : undefined
                                }
                                inputProps={{
                                  maxLength: 20,
                                }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                style={{
                                  width: "100%",
                                }}
                                fullWidth
                                disabled={!adminPerm}
                                id="outlined-basic"
                                label="OWS Flex Table"
                                variant="outlined"
                                name="OWSflexTable"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.OWSflexTable}
                                error={
                                  touched.OWSflexTable &&
                                  Boolean(errors.OWSflexTable)
                                }
                                helperText={
                                  touched.OWSflexTable &&
                                  typeof errors.OWSflexTable === "string"
                                    ? errors.OWSflexTable
                                    : undefined
                                }
                                inputProps={{
                                  maxLength: 20,
                                }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}></Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>BR Flex Table Rate</InputLabel>
                                <Select
                                  value={values.BRflexTableRate}
                                  disabled={!adminPerm}
                                  fullWidth
                                  id="outlined"
                                  label="BR Flex Table Rate"
                                  variant="outlined"
                                  name="BRflexTableRate"
                                  onChange={handleChange}
                                >
                                  {Object.values(
                                    this.state.hotel.ratePlans,
                                  ).map((rate: any) => {
                                    return (
                                      rate.modeOfSale === "OTA" && (
                                        <MenuItem key={rate.id} value={rate.id}>
                                          {rate.id}
                                        </MenuItem>
                                      )
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>OWS Flex Table Rate</InputLabel>
                                <Select
                                  value={values.OWSflexTableRate}
                                  disabled={!adminPerm}
                                  fullWidth
                                  id="outlined"
                                  label="OWS Flex Table Rate"
                                  variant="outlined"
                                  name="OWSflexTableRate"
                                  onChange={handleChange}
                                >
                                  {Object.values(
                                    this.state.hotel.ratePlans,
                                  ).map((rate: any) => {
                                    return (
                                      rate.modeOfSale === "OWS" && (
                                        <MenuItem key={rate.id} value={rate.id}>
                                          {rate.id}
                                        </MenuItem>
                                      )
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </div>
                    <br />

                    <SectionTitle {...{ value: "PMS Settings" }} />
                    <br />
                    <div style={{ flexGrow: "1" }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>PMS Provider</InputLabel>
                            <Select
                              value={values.pmsProvider}
                              disabled={
                                this.state.hotel.cm.id === "guestline" ||
                                !adminPerm
                              }
                              fullWidth
                              label="PMS Provider"
                              variant="outlined"
                              name="pmsProvider"
                              onChange={handleChange}
                            >
                              <MenuItem value="hotsoft">HotSoft</MenuItem>
                              <MenuItem value="guestline">Guestline</MenuItem>
                              <MenuItem value="none">None</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>

                        {this.state.hotel.pms.id !== "none" && (
                          <>
                            <Grid item xs={12} sm={6} md={4}>
                              <FormControl fullWidth>
                                <InputLabel>Inventory Type</InputLabel>
                                <Select
                                  value={values.inventoryType}
                                  disabled={
                                    this.state.hotel.cm.id === "guestline" ||
                                    !adminPerm
                                  }
                                  fullWidth
                                  label="Inventory Type"
                                  variant="outlined"
                                  name="inventoryType"
                                  onChange={handleChange}
                                >
                                  <MenuItem value="manual">Manual</MenuItem>
                                  <MenuItem value="live">Live</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}></Grid>
                            <Grid item xs={12} sm={6} md={4}></Grid>
                          </>
                        )}

                        {this.state.hotel.pms.id === "hotsoft" && (
                          <>
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                style={{
                                  width: "100%",
                                }}
                                fullWidth
                                disabled={!adminPerm}
                                id="outlined-basic"
                                label="Hotel Code"
                                variant="outlined"
                                name="pmsHotelCode"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.pmsHotelCode}
                                error={
                                  touched.pmsHotelCode &&
                                  Boolean(errors.pmsHotelCode)
                                }
                                helperText={
                                  touched.pmsHotelCode &&
                                  typeof errors.pmsHotelCode === "string"
                                    ? errors.pmsHotelCode
                                    : undefined
                                }
                                inputProps={{
                                  maxLength: 20,
                                }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                style={{
                                  width: "100%",
                                }}
                                disabled={!adminPerm}
                                fullWidth
                                id="outlined-basic"
                                label="URL Endpoint"
                                variant="outlined"
                                name="urlEndpoint"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.urlEndpoint}
                                error={
                                  touched.urlEndpoint &&
                                  Boolean(errors.urlEndpoint)
                                }
                                helperText={
                                  touched.urlEndpoint &&
                                  typeof errors.urlEndpoint === "string"
                                    ? errors.urlEndpoint
                                    : undefined
                                }
                                inputProps={{
                                  maxLength: 100,
                                  readOnly: adminPerm == false,
                                }}
                                required
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </div>
                    <br />

                    <SectionTitle {...{ value: "OWS Differential Settings" }} />
                    <br />
                    {typeof this.state.hotel.cm.channelConfig.ows !=
                      "undefined" && (
                      <div style={{ flexGrow: "1" }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              style={{
                                width: "100%",
                              }}
                              fullWidth
                              disabled={!revManagerPerm}
                              id="outlined-basic"
                              label="Default Label"
                              variant="outlined"
                              name="owsLabel"
                              inputProps={{
                                maxLength: 30,
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.owsLabel}
                              error={
                                touched.owsLabel && Boolean(errors.owsLabel)
                              }
                              helperText={
                                touched.owsLabel &&
                                typeof errors.owsLabel === "string"
                                  ? errors.owsLabel
                                  : undefined
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              style={{
                                width: "100%",
                              }}
                              fullWidth
                              disabled={!revManagerPerm}
                              id="outlined-basic"
                              label="Default Formula"
                              variant="outlined"
                              name="formula"
                              inputProps={{
                                maxLength: 20,
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.formula}
                              error={touched.formula && Boolean(errors.formula)}
                              helperText={
                                touched.formula &&
                                typeof errors.formula === "string"
                                  ? errors.formula
                                  : undefined
                              }
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}></Grid>
                        </Grid>
                      </div>
                    )}
                    <br />
                    {this.state.hotel.cm.channelConfig &&
                      typeof this.state.hotel.cm.channelConfig.ows !=
                        "undefined" &&
                      this.state.hotel.cm.channelConfig.ows && (
                        <Fragment>
                          <div className="form-check form-check-inline">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.seasonnalFormula}
                                  onChange={handleChange}
                                  name="seasonnalFormula"
                                  color="primary"
                                  disabled={!revManagerPerm}
                                />
                              }
                              label="OWS Overrides"
                            />
                          </div>
                        </Fragment>
                      )}
                    {this.state.hotel.cm.channelConfig &&
                      typeof this.state.hotel.cm.channelConfig.ows !=
                        "undefined" &&
                      typeof this.state.hotel.cm.channelConfig.ows
                        .seasonnalFormula != "undefined" &&
                      this.state.hotel.cm.channelConfig.ows
                        .seasonnalFormula && (
                        <Fragment>
                          <FormulaOverides
                            hotel={this.state.hotel}
                            key={this.state.currentSetIndex}
                            onUpdate={this.onHotelUpdate}
                            deleteFormula={this.state.deleteFormula}
                            isEdited={this.state.onSave}
                            isAdmin={this.props.user.permissions}
                            onDirty={() => {
                              setFieldValue(
                                "_formula_overrides_version",
                                values._formula_overrides_version + 1,
                                false,
                              );
                            }}
                          />
                        </Fragment>
                      )}
                    <br />
                    {!revManagerPerm ? (
                      <div></div>
                    ) : (
                      <div style={{ float: "right", marginBottom: "5vh" }}>
                        <Button
                          onClick={() => this.cancelChanges()}
                          color="secondary"
                          variant="contained"
                          style={{ marginLeft: "10px", marginTop: "10px" }}
                          type="button"
                        >
                          Cancel Changes
                        </Button>
                        <Button
                          color="primary"
                          variant="contained"
                          style={{ marginLeft: "10px", marginTop: "10px" }}
                          type="submit"
                          disabled={isSubmitting || !dirty}
                        >
                          Save Changes
                        </Button>
                      </div>
                    )}
                  </div>
                </form>
              );
            }}
          </Formik>
        </ThemeProvider>
      );
    }
  }
}

const mapStateToProps = (state: any) => {
  return {
    activeHotel: state.hotelData.activeHotel,
    user: state.user,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    setActiveHotel: (hotel: any) => dispatch(setActiveHotel(hotel)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(NewHotelConfig));
